import {
  type FormHTMLAttributes,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import styles from './Form.module.scss'
import { classNames } from '@/src/helpers/component'
import { gtmDataLayerPush } from '@/src/helpers/google'
import { getGoogleAnalyticsClientId, getSearchEngine, getSearchType } from '@/src/helpers/cookies'
import axios from 'axios'
import { deleteCookie } from 'cookies-next'
import type { Dict } from '@/src/helpers/common.types'

export type FormName =
  | 'contact_form'
  | 'work_with_us_form'
  | 'referralform'
  | 'lead_form'
  | 'supplierreferral'
  | 'newsletter_subscription'
// | 'popup_form'

export const formDataJSON = (form: HTMLFormElement) => {
  const formData = new FormData(form)
  const object: Dict<string | string[]> = {}

  formData.forEach((value, key) => {
    // If the key doesn't exist, just add the value
    if (!Reflect.has(object, key)) {
      object[key] = value as string
      return
    }

    // If the key exists and is not an array, convert it to an array
    if (!Array.isArray(object[key])) {
      object[key] = [object[key]!]
    }

    // Add the new value to the array
    object[key].push(value as string)
  })

  return object
}

export default function FormClient({
  children,
  afterChanged,
  dataForSubmission,
  beforeSubmit,
  afterSubmitted,
  formName,
  ...props
}: PropsWithChildren<
  FormHTMLAttributes<HTMLFormElement> & {
    afterChanged?: (arg0: {
      isComplete: boolean
      data: Dict<string | string[]>
      form: HTMLFormElement
    }) => void
    beforeSubmit?: (e: React.SubmitEvent<HTMLFormElement>) => boolean | void
    afterSubmitted?: (arg0: { success: boolean }) => void
    dataForSubmission?: () => Dict<string | string[]>
    formName: FormName
  }
>) {
  const refForm = useRef<HTMLFormElement>(null)
  const startedEditing = useRef(false)
  const [submitting, setSubmitting] = useState(false)

  const isComplete = useCallback(() => {
    return refForm.current?.checkValidity() ?? false
  }, [refForm])

  const callAfterChanged = useCallback(() => {
    afterChanged?.({
      isComplete: isComplete(),
      data: formDataJSON(refForm.current!),
      form: refForm.current!,
    })
  }, [afterChanged, isComplete])

  useEffect(() => {
    callAfterChanged()
  }, [callAfterChanged])

  const handleChange = useCallback(() => {
    callAfterChanged()
    if (!startedEditing.current) {
      startedEditing.current = true
      gtmDataLayerPush({ event: 'formStarted', formName })
    }
  }, [formName, callAfterChanged])

  useEffect(() => {
    const form = refForm.current
    if (!form) return
    form.addEventListener('change', handleChange)
    return () => form.removeEventListener('change', handleChange)
  }, [handleChange])

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    const form = refForm.current
    if (!form) return

    if (beforeSubmit?.(e) === false) return

    e.preventDefault()

    if (!form || submitting || !form.checkValidity() || !isComplete()) return
    setSubmitting(true)

    const data = dataForSubmission?.() ?? formDataJSON(form)

    axios
      .post('/api/submit-form/', {
        data: {
          formType: formName,
          data: {
            ...data,
            analytics_data: {
              gaClientId: await getGoogleAnalyticsClientId(),
              searchEngine: getSearchEngine(),
              searchType: await getSearchType(),
              referrerUrl: document.referrer,
              documentUrl: document.URL,
            },
          },
        },
      })
      .then(() => {
        gtmDataLayerPush({ event: 'formComplete', formName })
        gtmDataLayerPush({ event: 'formCompleteWithClientId', formName })

        deleteCookie('ag_srch_eng')
        deleteCookie('ag_pd')

        afterSubmitted?.({ success: true })
        setSubmitting(false)
      })
      .catch(e => {
        console.error(e)
        afterSubmitted?.({ success: false })
        setSubmitting(false)
      })
  }

  return (
    <form
      {...props}
      className={classNames(styles.container, submitting ? styles.submitting : '', props.className)}
      ref={refForm}
      onChange={handleChange}
      onSubmit={handleSubmit}>
      {children}
    </form>
  )
}
